import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { Filter } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { formatDate, formatRelative } from '@/lib/format';

const searchSchema = z.object({
  q: z.string().optional(),
  actor: z.string().optional(),
  action: z.string().optional(),
});

export const Route = createFileRoute('/_authed/audit-logs')({
  validateSearch: (input): { q?: string; actor?: string; action?: string } => {
    const r = searchSchema.safeParse(input);
    return r.success ? r.data : {};
  },
  component: AuditLogsPage,
});

interface AuditEvent {
  id: string;
  actor: string;
  action: string;
  target: string;
  ip: string;
  userAgent: string;
  ts: number;
}

const mock: AuditEvent[] = Array.from({ length: 30 }, (_, i) => ({
  id: `ae_${i}`,
  actor: ['admin@cms.express', 'system', 'soporte@cms.express'][i % 3] as string,
  action: [
    'user.login',
    'site.create',
    'site.update',
    'billing.charge',
    'user.suspend',
    'settings.update',
  ][i % 6] as string,
  target: `target_${i}`,
  ip: `192.0.2.${i}`,
  userAgent: 'Mozilla/5.0',
  ts: Date.now() - i * 600_000,
}));

function AuditLogsPage() {
  const search = Route.useSearch();
  const { data, isLoading } = useQuery({
    queryKey: ['audit', search],
    queryFn: async () => {
      try {
        return await api.get<{ items: AuditEvent[] }>('/admin/audit', { query: search });
      } catch {
        return { items: mock };
      }
    },
  });
  const events = data?.items ?? mock;

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Auditoría</h1>
        <p className="text-muted-foreground">
          Log append-only con todos los eventos administrativos
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Eventos</CardTitle>
          <CardDescription>{events.length} eventos</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-3 flex gap-2">
            <Input placeholder="Buscar…" aria-label="Buscar" />
            <Button variant="outline" size="sm">
              <Filter className="mr-2 h-4 w-4" /> Filtros
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Acción</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>IP</TableHead>
                <TableHead>User agent</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading
                ? Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={6}>
                        <Skeleton className="h-5 w-full" />
                      </TableCell>
                    </TableRow>
                  ))
                : events.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell>
                        <span title={formatDate(e.ts, 'es')}>
                          {formatRelative(e.ts, 'es')}
                        </span>
                      </TableCell>
                      <TableCell className="font-medium">{e.actor}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{e.action}</Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{e.target}</TableCell>
                      <TableCell className="font-mono text-xs">{e.ip}</TableCell>
                      <TableCell className="max-w-xs truncate text-xs text-muted-foreground">
                        {e.userAgent}
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
